/**
 * Zone Creation Wizard Component
 *
 * A step-by-step wizard for creating new thermal zones in the building model.
 * Supports simple box creation and footprint extrusion methods.
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useGeometryEditStore, useZoneWizard } from '../../geometryEditStore';
import { useIDFStore } from '../../../stores/idfStore';
import type {
  ZoneCreationMethod,
  SimpleBoxConfig,
  FootprintExtrudeConfig,
  ZoneConstructions,
  Point2D,
} from '../../editingTypes';
import { createSimpleBoxZoneIDFObjects, createExtrudedZoneIDFObjects } from '../../sync/zoneSync';

/**
 * Mini 3D Preview Canvas for the zone wizard.
 */
function ZonePreviewCanvas({
  length,
  width,
  height,
}: {
  length: number;
  width: number;
  height: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const w = canvas.width;
    const h = canvas.height;

    // Clear
    ctx.fillStyle = 'hsl(var(--muted))';
    ctx.fillRect(0, 0, w, h);

    // Calculate isometric projection
    const scale = Math.min(w, h) / (Math.max(length, width, height) * 3);
    const isoAngle = Math.PI / 6; // 30 degrees
    const centerX = w / 2;
    const centerY = h / 2 + 20;

    // Helper to project 3D to 2D isometric
    const project = (x: number, y: number, z: number) => ({
      x: centerX + (x - y) * Math.cos(isoAngle) * scale,
      y: centerY - z * scale + (x + y) * Math.sin(isoAngle) * scale,
    });

    // Box vertices (origin at 0,0,0)
    const vertices = [
      [0, 0, 0],
      [length, 0, 0],
      [length, width, 0],
      [0, width, 0],
      [0, 0, height],
      [length, 0, height],
      [length, width, height],
      [0, width, height],
    ] as const;

    // Project all vertices
    const projected = vertices.map((v) => project(v[0], v[1], v[2]));

    // Draw faces (back to front for proper occlusion)

    // Left face (darker)
    ctx.beginPath();
    ctx.moveTo(projected[0]!.x, projected[0]!.y);
    ctx.lineTo(projected[3]!.x, projected[3]!.y);
    ctx.lineTo(projected[7]!.x, projected[7]!.y);
    ctx.lineTo(projected[4]!.x, projected[4]!.y);
    ctx.closePath();
    ctx.fillStyle = 'hsl(var(--primary) / 0.3)';
    ctx.fill();
    ctx.strokeStyle = 'hsl(var(--primary))';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Right face (medium)
    ctx.beginPath();
    ctx.moveTo(projected[1]!.x, projected[1]!.y);
    ctx.lineTo(projected[2]!.x, projected[2]!.y);
    ctx.lineTo(projected[6]!.x, projected[6]!.y);
    ctx.lineTo(projected[5]!.x, projected[5]!.y);
    ctx.closePath();
    ctx.fillStyle = 'hsl(var(--primary) / 0.4)';
    ctx.fill();
    ctx.stroke();

    // Top face (lightest)
    ctx.beginPath();
    ctx.moveTo(projected[4]!.x, projected[4]!.y);
    ctx.lineTo(projected[5]!.x, projected[5]!.y);
    ctx.lineTo(projected[6]!.x, projected[6]!.y);
    ctx.lineTo(projected[7]!.x, projected[7]!.y);
    ctx.closePath();
    ctx.fillStyle = 'hsl(var(--primary) / 0.5)';
    ctx.fill();
    ctx.stroke();

    // Front face
    ctx.beginPath();
    ctx.moveTo(projected[0]!.x, projected[0]!.y);
    ctx.lineTo(projected[1]!.x, projected[1]!.y);
    ctx.lineTo(projected[5]!.x, projected[5]!.y);
    ctx.lineTo(projected[4]!.x, projected[4]!.y);
    ctx.closePath();
    ctx.fillStyle = 'hsl(var(--primary) / 0.35)';
    ctx.fill();
    ctx.stroke();

    // Draw dimension labels
    ctx.fillStyle = 'hsl(var(--foreground))';
    ctx.font = '11px system-ui, sans-serif';
    ctx.textAlign = 'center';

    // Length label (X)
    const lengthMid = project(length / 2, 0, -1);
    ctx.fillText(`${length}m`, lengthMid.x, lengthMid.y + 15);

    // Width label (Y)
    const widthMid = project(length + 0.5, width / 2, 0);
    ctx.fillText(`${width}m`, widthMid.x + 10, widthMid.y);

    // Height label (Z)
    const heightMid = project(-0.5, 0, height / 2);
    ctx.fillText(`${height}m`, heightMid.x - 15, heightMid.y);
  }, [length, width, height]);

  return (
    <canvas
      ref={canvasRef}
      width={200}
      height={160}
      className="rounded-lg border border-border"
      aria-label={`3D preview of zone: ${length}m x ${width}m x ${height}m`}
    />
  );
}

/**
 * Props for ZoneCreationWizard.
 */
interface ZoneCreationWizardProps {
  onComplete?: (zoneId: string) => void;
  onCancel?: () => void;
  onOpenFloorPlan?: () => void;
}

/**
 * Zone Creation Wizard Component.
 */
export function ZoneCreationWizard({ onComplete, onCancel, onOpenFloorPlan }: ZoneCreationWizardProps) {
  const wizard = useZoneWizard();
  const closeZoneWizard = useGeometryEditStore((s) => s.closeZoneWizard);
  const addObject = useIDFStore((s) => s.addObject);
  const addNotification = useGeometryEditStore((s) => s.addNotification);
  const recordAction = useGeometryEditStore((s) => s.recordAction);

  // Local state for form
  const [method, setMethod] = useState<ZoneCreationMethod>('simple-box');
  const [zoneName, setZoneName] = useState('New Zone');
  const [_step, _setStep] = useState(0);

  // Simple box dimensions
  const [length, setLength] = useState(10);
  const [width, setWidth] = useState(6);
  const [height, setHeight] = useState(3);
  const [originX, setOriginX] = useState(0);
  const [originY, setOriginY] = useState(0);
  const [originZ, setOriginZ] = useState(0);

  // Footprint state (for extrusion)
  const [footprint, _setFootprint] = useState<Point2D[]>([]);
  const [baseZ, setBaseZ] = useState(0);
  const [extrudeHeight, setExtrudeHeight] = useState(3);

  // Construction selections
  const [exteriorWalls, setExteriorWalls] = useState('');
  const [interiorWalls, setInteriorWalls] = useState('');
  const [floor, setFloor] = useState('');
  const [ceiling, setCeiling] = useState('');

  // Get available constructions from IDF
  const document = useIDFStore((s) => s.document);
  const availableConstructions = useMemo(() => {
    if (!document) return [];
    return document.objects
      .filter((obj) => obj.className.toLowerCase() === 'construction')
      .map((obj) => obj.fields[0]?.value ?? '')
      .filter(Boolean);
  }, [document]);

  const handleClose = useCallback(() => {
    closeZoneWizard();
    onCancel?.();
  }, [closeZoneWizard, onCancel]);

  const handleCreate = useCallback(() => {
    const constructions: ZoneConstructions = {
      exteriorWalls: exteriorWalls || 'Exterior Wall',
      interiorWalls: interiorWalls || 'Interior Wall',
      floor: floor || 'Floor',
      ceiling: ceiling || 'Ceiling',
    };

    let result;
    let config: SimpleBoxConfig | FootprintExtrudeConfig;

    if (method === 'simple-box') {
      config = {
        name: zoneName,
        origin: { x: originX, y: originY, z: originZ },
        dimensions: { length, width, height },
        constructions,
      };
      result = createSimpleBoxZoneIDFObjects(config);
    } else if (method === 'extrude-footprint') {
      if (footprint.length < 3) {
        addNotification('error', 'Please draw at least 3 vertices for the footprint');
        return;
      }
      config = {
        name: zoneName,
        footprint,
        baseZ,
        height: extrudeHeight,
        constructions,
      };
      result = createExtrudedZoneIDFObjects(config);
    } else {
      return;
    }

    // Add zone object to IDF
    const zoneId = addObject(result.zone);

    // Add surface objects to IDF
    const surfaceIds: string[] = [];
    for (const surface of result.surfaces) {
      const surfaceId = addObject(surface);
      surfaceIds.push(surfaceId);
    }

    // Record action for undo/redo
    recordAction({
      type: 'zone-create',
      description: `Create zone "${zoneName}"`,
      data: { config, zoneId },
      inverseData: { zoneId, surfaceIds },
      createdIds: [zoneId, ...surfaceIds],
      deletedIds: [],
    });

    addNotification('success', `Zone "${zoneName}" created with ${result.surfaces.length} surfaces`);
    closeZoneWizard();
    onComplete?.(zoneId);
  }, [
    method,
    zoneName,
    length,
    width,
    height,
    originX,
    originY,
    originZ,
    footprint,
    baseZ,
    extrudeHeight,
    exteriorWalls,
    interiorWalls,
    floor,
    ceiling,
    addObject,
    recordAction,
    addNotification,
    closeZoneWizard,
    onComplete,
  ]);

  // Calculate preview dimensions
  const previewVolume = useMemo(() => {
    if (method === 'simple-box') {
      return length * width * height;
    } else if (footprint.length >= 3) {
      // Calculate polygon area
      let area = 0;
      for (let i = 0; i < footprint.length; i++) {
        const p1 = footprint[i];
        const p2 = footprint[(i + 1) % footprint.length];
        if (p1 && p2) {
          area += p1.x * p2.y - p2.x * p1.y;
        }
      }
      return Math.abs(area / 2) * extrudeHeight;
    }
    return 0;
  }, [method, length, width, height, footprint, extrudeHeight]);

  if (!wizard.isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/70"
      role="dialog"
      aria-modal="true"
      aria-labelledby="zone-wizard-title"
    >
      <div className="w-full max-w-2xl rounded-lg border border-border bg-card shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 id="zone-wizard-title" className="text-lg font-semibold text-foreground">Create New Zone</h2>
          <button
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background rounded"
            aria-label="Close dialog"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {/* Zone Name */}
          <div className="mb-4">
            <label htmlFor="zone-name" className="mb-1 block text-sm font-medium text-foreground">
              Zone Name
            </label>
            <input
              type="text"
              id="zone-name"
              value={zoneName}
              onChange={(e) => setZoneName(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="Enter zone name..."
            />
          </div>

          {/* Creation Method */}
          <fieldset className="mb-6">
            <legend className="mb-2 block text-sm font-medium text-foreground">Creation Method</legend>
            <div className="space-y-2">
              <label className="flex cursor-pointer items-center rounded-md border border-border bg-background p-3 transition-colors hover:bg-muted has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                <input
                  type="radio"
                  name="method"
                  checked={method === 'simple-box'}
                  onChange={() => setMethod('simple-box')}
                  className="h-4 w-4 text-primary focus:ring-primary"
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-foreground">Simple Box</span>
                  <p className="text-xs text-muted-foreground">Specify length, width, and height dimensions</p>
                </div>
              </label>
              <label className="flex cursor-pointer items-center rounded-md border border-border bg-background p-3 transition-colors hover:bg-muted has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                <input
                  type="radio"
                  name="method"
                  checked={method === 'extrude-footprint'}
                  onChange={() => setMethod('extrude-footprint')}
                  className="h-4 w-4 text-primary focus:ring-primary"
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-foreground">Extrude Footprint</span>
                  <p className="text-xs text-muted-foreground">Draw a 2D polygon and extrude to height</p>
                </div>
              </label>
            </div>
          </fieldset>

          <hr className="mb-4 border-border" />

          {/* Simple Box Configuration */}
          {method === 'simple-box' && (
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="mb-3 text-sm font-medium text-foreground">Dimensions</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <label htmlFor="length-input" className="w-24 text-sm text-muted-foreground">Length (X):</label>
                    <input
                      type="number"
                      id="length-input"
                      value={length}
                      onChange={(e) => setLength(parseFloat(e.target.value) || 0)}
                      className="w-24 rounded border border-border bg-background px-2 py-1 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                      step="0.1"
                      min="0.1"
                    />
                    <span className="ml-2 text-sm text-muted-foreground">m</span>
                  </div>
                  <div className="flex items-center">
                    <label htmlFor="width-input" className="w-24 text-sm text-muted-foreground">Width (Y):</label>
                    <input
                      type="number"
                      id="width-input"
                      value={width}
                      onChange={(e) => setWidth(parseFloat(e.target.value) || 0)}
                      className="w-24 rounded border border-border bg-background px-2 py-1 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                      step="0.1"
                      min="0.1"
                    />
                    <span className="ml-2 text-sm text-muted-foreground">m</span>
                  </div>
                  <div className="flex items-center">
                    <label htmlFor="height-input" className="w-24 text-sm text-muted-foreground">Height (Z):</label>
                    <input
                      type="number"
                      id="height-input"
                      value={height}
                      onChange={(e) => setHeight(parseFloat(e.target.value) || 0)}
                      className="w-24 rounded border border-border bg-background px-2 py-1 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                      step="0.1"
                      min="0.1"
                    />
                    <span className="ml-2 text-sm text-muted-foreground">m</span>
                  </div>
                </div>

                <h3 className="mb-3 mt-4 text-sm font-medium text-foreground">Origin</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <label htmlFor="origin-x" className="w-24 text-sm text-muted-foreground">Origin X:</label>
                    <input
                      type="number"
                      id="origin-x"
                      value={originX}
                      onChange={(e) => setOriginX(parseFloat(e.target.value) || 0)}
                      className="w-24 rounded border border-border bg-background px-2 py-1 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                      step="0.1"
                    />
                    <span className="ml-2 text-sm text-muted-foreground">m</span>
                  </div>
                  <div className="flex items-center">
                    <label htmlFor="origin-y" className="w-24 text-sm text-muted-foreground">Origin Y:</label>
                    <input
                      type="number"
                      id="origin-y"
                      value={originY}
                      onChange={(e) => setOriginY(parseFloat(e.target.value) || 0)}
                      className="w-24 rounded border border-border bg-background px-2 py-1 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                      step="0.1"
                    />
                    <span className="ml-2 text-sm text-muted-foreground">m</span>
                  </div>
                  <div className="flex items-center">
                    <label htmlFor="origin-z" className="w-24 text-sm text-muted-foreground">Origin Z:</label>
                    <input
                      type="number"
                      id="origin-z"
                      value={originZ}
                      onChange={(e) => setOriginZ(parseFloat(e.target.value) || 0)}
                      className="w-24 rounded border border-border bg-background px-2 py-1 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                      step="0.1"
                    />
                    <span className="ml-2 text-sm text-muted-foreground">m</span>
                  </div>
                </div>
              </div>

              {/* Live 3D Preview */}
              <div>
                <h3 className="mb-3 text-sm font-medium text-foreground">Preview</h3>
                <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-muted/50 p-4">
                  <ZonePreviewCanvas length={length} width={width} height={height} />
                  <div className="mt-3 text-center">
                    <div className="text-sm font-medium text-foreground">
                      {length}m × {width}m × {height}m
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Volume: {previewVolume.toFixed(1)} m³
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Floor Area: {(length * width).toFixed(1)} m²
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Footprint Extrusion Configuration */}
          {method === 'extrude-footprint' && (
            <div>
              <h3 className="mb-3 text-sm font-medium text-foreground">Footprint Extrusion</h3>
              <p className="mb-4 text-sm text-muted-foreground">
                Draw a 2D footprint in the Floor Plan Editor, then configure the height and constructions here.
              </p>

              {/* Open 2D Editor Button */}
              <button
                onClick={() => {
                  onOpenFloorPlan?.();
                  handleClose();
                }}
                className="mb-4 flex w-full items-center justify-center gap-2 rounded-md border-2 border-dashed border-primary/50 bg-primary/5 px-4 py-3 text-sm font-medium text-primary transition-colors hover:border-primary hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                </svg>
                Open 2D Floor Plan Editor
              </button>

              <div className="mb-4 grid grid-cols-2 gap-4">
                <div className="flex items-center">
                  <label htmlFor="base-z" className="w-24 text-sm text-muted-foreground">Base Z:</label>
                  <input
                    type="number"
                    id="base-z"
                    value={baseZ}
                    onChange={(e) => setBaseZ(parseFloat(e.target.value) || 0)}
                    className="w-24 rounded border border-border bg-background px-2 py-1 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    step="0.1"
                  />
                  <span className="ml-2 text-sm text-muted-foreground">m</span>
                </div>
                <div className="flex items-center">
                  <label htmlFor="extrude-height" className="w-24 text-sm text-muted-foreground">Height:</label>
                  <input
                    type="number"
                    id="extrude-height"
                    value={extrudeHeight}
                    onChange={(e) => setExtrudeHeight(parseFloat(e.target.value) || 0)}
                    className="w-24 rounded border border-border bg-background px-2 py-1 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    step="0.1"
                    min="0.1"
                  />
                  <span className="ml-2 text-sm text-muted-foreground">m</span>
                </div>
              </div>

              <div className="rounded-lg border border-border bg-muted/50 p-4 text-center">
                {footprint.length === 0 ? (
                  <div className="flex flex-col items-center gap-2">
                    <svg className="h-8 w-8 text-muted-foreground/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-sm text-muted-foreground">
                      No footprint defined yet
                    </span>
                    <span className="text-xs text-muted-foreground/70">
                      Click "Open 2D Floor Plan Editor" to draw
                    </span>
                  </div>
                ) : (
                  <div className="text-sm text-foreground">
                    <span className="font-medium">Footprint:</span> {footprint.length} vertices
                    <br />
                    <span className="text-muted-foreground">Volume: {previewVolume.toFixed(1)} m³</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Constructions */}
          <div className="mt-6">
            <h3 className="mb-3 text-sm font-medium text-foreground">Constructions</h3>
            {availableConstructions.length === 0 ? (
              <div className="rounded-lg border border-border bg-muted/50 p-3 text-center text-sm text-muted-foreground">
                <svg className="mx-auto mb-2 h-6 w-6 text-muted-foreground/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                No constructions defined in the IDF file. Default names will be used.
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="ext-walls" className="mb-1 block text-xs text-muted-foreground">Exterior Walls</label>
                  <select
                    id="ext-walls"
                    value={exteriorWalls}
                    onChange={(e) => setExteriorWalls(e.target.value)}
                    className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="">Select construction...</option>
                    {availableConstructions.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="int-walls" className="mb-1 block text-xs text-muted-foreground">Interior Walls</label>
                  <select
                    id="int-walls"
                    value={interiorWalls}
                    onChange={(e) => setInteriorWalls(e.target.value)}
                    className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="">Select construction...</option>
                    {availableConstructions.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="floor-const" className="mb-1 block text-xs text-muted-foreground">Floor</label>
                  <select
                    id="floor-const"
                    value={floor}
                    onChange={(e) => setFloor(e.target.value)}
                    className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="">Select construction...</option>
                    {availableConstructions.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="ceiling-const" className="mb-1 block text-xs text-muted-foreground">Ceiling/Roof</label>
                  <select
                    id="ceiling-const"
                    value={ceiling}
                    onChange={(e) => setCeiling(e.target.value)}
                    className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="">Select construction...</option>
                    {availableConstructions.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-border bg-muted/50 px-6 py-4">
          <button
            onClick={handleClose}
            className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!zoneName.trim() || (method === 'extrude-footprint' && footprint.length < 3)}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background"
          >
            Create Zone
          </button>
        </div>
      </div>
    </div>
  );
}

export default ZoneCreationWizard;
